import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RootLayoutContent } from '@/app/layouts/RootLayout';
import { NavigationConfig } from '@/app/types/navigation';
import { PAGE_SIZES } from '@/common/components/ui/pagination-controls';
import { usePagesNavigation } from '@/common/hooks/usePagesNavigation';
import { RenewalList } from '@/renewal/component/RenewalList';
import { useFetchPaginatedRenewalList } from '@/renewal/hooks/useFetchPaginatedRenewalList';
import { Filters } from '@/renewal/types/FilterTypes';
import { RenewalSortField, SortOrder } from '@/renewal/types/SortTypes';

export default function RenewalPrioritizerPage() {
  const { t } = useTranslation();
  const { pages, currentPage } = usePagesNavigation();

  const [pagination, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
    sortBy: RenewalSortField;
    sortOrder: SortOrder;
  }>({
    pageIndex: 0,
    pageSize: PAGE_SIZES[2],
    sortBy: 'expiration',
    sortOrder: 'asc',
  });
  const [filters, setFilters] = useState<Filters>({});

  const handleFilterChange = (
    value: string | string[] | number[],
    key: string,
  ) => {
    const isReset =
      value === 'all' ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);
    setFilters((prev) => ({ ...prev, [key]: isReset ? undefined : value }));
  };

  const clearFilters = () => setFilters({});

  const { paginatedRenewals, isLoading } = useFetchPaginatedRenewalList(
    pagination,
    filters,
  );
  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };
  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));
  };
  const handleSort = (field: RenewalSortField) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const navigationConfig: NavigationConfig = {
    title: t('renewalPrioritizer'),
    backAction: { hasBackAction: false },
    pageNavigation: {
      currentPage,
      pages,
    },
  };

  return (
    <RootLayoutContent navigationConfig={navigationConfig}>
      <div className="flex flex-col h-full w-full">
        <RenewalList
          renewals={paginatedRenewals.items}
          isLoading={isLoading}
          totalPages={paginatedRenewals.totalPages}
          totalRenewals={paginatedRenewals.totalItems}
          currentPage={pagination.pageIndex}
          pageSize={pagination.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
        />
      </div>
    </RootLayoutContent>
  );
}
